import React from 'react';
import { Document, Page, Text, View, StyleSheet, Image } from '@react-pdf/renderer';

const styles = StyleSheet.create({
  page: { padding: 35, fontSize: 9, fontFamily: 'Helvetica', color: '#333' },
  header: { flexDirection: 'row', justifyContent: 'space-between', marginBottom: 15, paddingBottom: 15, borderBottomWidth: 2, borderBottomColor: '#333' },
  title: { fontSize: 22, fontWeight: 'bold', fontFamily: 'Helvetica-Bold' },
  invoiceNumber: { fontSize: 11, marginTop: 2 },
  twoColumn: { flexDirection: 'row', justifyContent: 'space-between', marginBottom: 15 },
  column: { flex: 1 },
  sectionTitle: { fontSize: 8, color: '#666', textTransform: 'uppercase', marginBottom: 4, letterSpacing: 1, fontFamily: 'Helvetica-Bold' },
  bold: { fontWeight: 'bold', fontFamily: 'Helvetica-Bold' },
  // Info box
  infoBox: { backgroundColor: '#f5f5f5', padding: 10, borderRadius: 3, marginBottom: 15 },
  infoRow: { flexDirection: 'row', justifyContent: 'space-between', paddingVertical: 2 },
  // Table
  tableHeader: { flexDirection: 'row', borderBottomWidth: 1.5, borderBottomColor: '#333', paddingBottom: 6, marginBottom: 4, marginTop: 10 },
  tableRow: { flexDirection: 'row', paddingVertical: 5, borderBottomWidth: 0.5, borderBottomColor: '#ddd' },
  tableRowAlt: { backgroundColor: '#fafafa' },
  colNum: { width: 25, textAlign: 'center' },
  colDescription: { flex: 3, paddingLeft: 5 },
  colQty: { flex: 0.7, textAlign: 'center' },
  colPrice: { flex: 1, textAlign: 'right' },
  colAmount: { flex: 1, textAlign: 'right' },
  // Totals
  totalsSection: { flexDirection: 'row', justifyContent: 'flex-end', marginTop: 10 },
  totalsBox: { width: 240, borderWidth: 1, borderColor: '#ddd', borderRadius: 3, padding: 10 },
  totalRow: { flexDirection: 'row', justifyContent: 'space-between', paddingVertical: 3 },
  grandTotal: { flexDirection: 'row', justifyContent: 'space-between', paddingVertical: 6, borderTopWidth: 1.5, borderTopColor: '#333', marginTop: 4 },
  grandTotalText: { fontSize: 13, fontWeight: 'bold', fontFamily: 'Helvetica-Bold' },
  // Bank
  bankBox: { borderWidth: 1, borderColor: '#333', borderRadius: 3, padding: 12, marginTop: 15 },
  bankTitle: { fontSize: 10, fontFamily: 'Helvetica-Bold', marginBottom: 6, textTransform: 'uppercase', letterSpacing: 1 },
  bankRow: { flexDirection: 'row', marginBottom: 3 },
  bankLabel: { width: 100, color: '#666' },
  // Notes
  notesBox: { marginTop: 15, padding: 10, backgroundColor: '#f9f9f9', borderRadius: 3, borderLeftWidth: 3, borderLeftColor: '#333' },
  // Footer
  footer: { position: 'absolute', bottom: 25, left: 35, right: 35 },
  footerLine: { borderTopWidth: 0.5, borderTopColor: '#ccc', paddingTop: 8, flexDirection: 'row', justifyContent: 'space-between' },
  footerText: { fontSize: 7, color: '#999' },
  logo: { width: 55, height: 55, objectFit: 'contain', marginBottom: 4 },
});

const formatCurrency = (amount) =>
  new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD' }).format(amount || 0);

const formatAddress = (addr) => {
  if (!addr) return [];
  const parts = [];
  if (addr.street) parts.push(addr.street);
  const line2 = [addr.city, addr.state, addr.postcode].filter(Boolean).join(' ');
  if (line2) parts.push(line2);
  if (addr.country && addr.country !== 'Australia') parts.push(addr.country);
  return parts;
};

const DetailedTemplate = ({ invoice }) => {
  const issuer = invoice.issuerSnapshot || {};
  const org = invoice.organisationSnapshot || {};

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {/* Header */}
        <View style={styles.header}>
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
            {issuer.logoUrl && <Image src={issuer.logoUrl} style={styles.logo} />}
            <View>
              <Text style={styles.title}>TAX INVOICE</Text>
              <Text style={styles.invoiceNumber}>{invoice.invoiceNumber}</Text>
            </View>
          </View>
          <View style={{ textAlign: 'right', maxWidth: 220 }}>
            <Text style={[styles.bold, { fontSize: 11 }]}>{issuer.businessName || issuer.name || ''}</Text>
            {issuer.abn && <Text>ABN: {issuer.abn}</Text>}
            {formatAddress(issuer.address).map((line, i) => (
              <Text key={i}>{line}</Text>
            ))}
            {issuer.phone && <Text>Ph: {issuer.phone}</Text>}
            {issuer.email && <Text>{issuer.email}</Text>}
          </View>
        </View>

        {/* Two columns: Bill To + Invoice Details */}
        <View style={styles.twoColumn}>
          <View style={styles.column}>
            <Text style={styles.sectionTitle}>Bill To</Text>
            <Text style={[styles.bold, { fontSize: 10 }]}>{org.name || ''}</Text>
            {org.abn && <Text>ABN: {org.abn}</Text>}
            {org.contactPerson && <Text>Attention: {org.contactPerson}</Text>}
            {formatAddress(org.address).map((line, i) => (
              <Text key={i}>{line}</Text>
            ))}
            {org.phone && <Text>Ph: {org.phone}</Text>}
            {org.email && <Text>{org.email}</Text>}
          </View>
          <View style={{ width: 200 }}>
            <View style={styles.infoBox}>
              <View style={styles.infoRow}>
                <Text style={styles.bold}>Invoice No:</Text>
                <Text>{invoice.invoiceNumber}</Text>
              </View>
              <View style={styles.infoRow}>
                <Text style={styles.bold}>Issue Date:</Text>
                <Text>{invoice.issueDate ? new Date(invoice.issueDate).toLocaleDateString('en-AU') : ''}</Text>
              </View>
              <View style={styles.infoRow}>
                <Text style={styles.bold}>Due Date:</Text>
                <Text>{invoice.dueDate ? new Date(invoice.dueDate).toLocaleDateString('en-AU') : ''}</Text>
              </View>
              <View style={styles.infoRow}>
                <Text style={styles.bold}>Currency:</Text>
                <Text>{invoice.currency || 'AUD'}</Text>
              </View>
              {issuer.paymentTerms && (
                <View style={styles.infoRow}>
                  <Text style={styles.bold}>Terms:</Text>
                  <Text>{issuer.paymentTerms}</Text>
                </View>
              )}
            </View>
          </View>
        </View>

        {/* Line Items Table */}
        <View style={styles.tableHeader}>
          <Text style={[styles.colNum, styles.bold]}>#</Text>
          <Text style={[styles.colDescription, styles.bold]}>Description</Text>
          <Text style={[styles.colQty, styles.bold]}>Qty</Text>
          <Text style={[styles.colPrice, styles.bold]}>Unit Price</Text>
          <Text style={[styles.colAmount, styles.bold]}>Amount</Text>
        </View>
        {(invoice.lineItems || []).map((item, idx) => (
          <View key={idx} style={[styles.tableRow, idx % 2 === 1 ? styles.tableRowAlt : {}]}>
            <Text style={styles.colNum}>{idx + 1}</Text>
            <Text style={styles.colDescription}>{item.description}</Text>
            <Text style={styles.colQty}>{item.quantity}</Text>
            <Text style={styles.colPrice}>{formatCurrency(item.unitPrice)}</Text>
            <Text style={styles.colAmount}>{formatCurrency(item.amount)}</Text>
          </View>
        ))}

        {/* Totals */}
        <View style={styles.totalsSection}>
          <View style={styles.totalsBox}>
            <View style={styles.totalRow}>
              <Text>Subtotal</Text>
              <Text>{formatCurrency(invoice.subtotal)}</Text>
            </View>
            {invoice.gstIncluded && (
              <View style={styles.totalRow}>
                <Text>GST ({invoice.gstRate || 10}%)</Text>
                <Text>{formatCurrency(invoice.gstAmount)}</Text>
              </View>
            )}
            {!invoice.gstIncluded && (
              <View style={styles.totalRow}>
                <Text style={{ color: '#999' }}>No GST applicable</Text>
                <Text>$0.00</Text>
              </View>
            )}
            <View style={styles.grandTotal}>
              <Text style={styles.grandTotalText}>TOTAL DUE</Text>
              <Text style={styles.grandTotalText}>{formatCurrency(invoice.total)}</Text>
            </View>
          </View>
        </View>

        {/* Bank Details — prominent section */}
        {issuer.bankDetails && (issuer.bankDetails.bsb || issuer.bankDetails.accountNumber) && (
          <View style={styles.bankBox}>
            <Text style={styles.bankTitle}>Payment Details</Text>
            {issuer.bankDetails.bankName && (
              <View style={styles.bankRow}>
                <Text style={styles.bankLabel}>Bank:</Text>
                <Text style={styles.bold}>{issuer.bankDetails.bankName}</Text>
              </View>
            )}
            {issuer.bankDetails.accountName && (
              <View style={styles.bankRow}>
                <Text style={styles.bankLabel}>Account Name:</Text>
                <Text style={styles.bold}>{issuer.bankDetails.accountName}</Text>
              </View>
            )}
            {issuer.bankDetails.bsb && (
              <View style={styles.bankRow}>
                <Text style={styles.bankLabel}>BSB:</Text>
                <Text style={styles.bold}>{issuer.bankDetails.bsb}</Text>
              </View>
            )}
            {issuer.bankDetails.accountNumber && (
              <View style={styles.bankRow}>
                <Text style={styles.bankLabel}>Account Number:</Text>
                <Text style={styles.bold}>{issuer.bankDetails.accountNumber}</Text>
              </View>
            )}
            <Text style={{ marginTop: 6, fontSize: 8, color: '#666' }}>
              Please use invoice number {invoice.invoiceNumber} as the payment reference.
            </Text>
          </View>
        )}

        {/* Notes */}
        {invoice.notes && (
          <View style={styles.notesBox}>
            <Text style={[styles.sectionTitle, { color: '#333' }]}>Notes</Text>
            <Text>{invoice.notes}</Text>
          </View>
        )}

        {/* Footer */}
        <View style={styles.footer}>
          <View style={styles.footerLine}>
            <Text style={styles.footerText}>{issuer.businessName || issuer.name || ''}</Text>
            <Text style={styles.footerText}>{invoice.invoiceNumber}</Text>
            <Text style={styles.footerText}>Page 1 of 1</Text>
          </View>
        </View>
      </Page>
    </Document>
  );
};

export default DetailedTemplate;
